import { type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { showToast } from "../primitives";
import { modalOverlay, modalBox } from "./task-modal-shared";

interface PromptDisplayModalProps {
  open: boolean;
  onClose: () => void;
  /** Header title, e.g. "Plan with Engineering" or "Verify spec". */
  title: string;
  /** Lighter sub-line under the title. */
  subtitle?: string;
  /** Header icon (Tabler name without `ti-`). */
  icon?: string;
  /** MCP prompt name to compose via `prompts/get`. */
  promptName: string;
  /** Arguments substituted into the prompt. */
  args?: Record<string, string>;
}

/**
 * Sprint 3 — the generalised prompt hand-off modal (refactored from Sprint 8's
 * PlanWithAgentModal). Composes any MCP prompt via `prompts/get` and surfaces it
 * for copy or hand-off to Claude / Cursor. The Cmd-K "Run prompt" flow and the
 * PlanWithAgentModal wrapper both render this; future prompt-driven UX reuses it.
 *
 * Per Sprint 8 Q4C the host buttons copy + guide rather than deep-link.
 */
export function PromptDisplayModal({
  open,
  onClose,
  title,
  subtitle,
  icon = "wand",
  promptName,
  args = {},
}: PromptDisplayModalProps) {
  const promptQuery = useQuery<string>({
    // args are part of the identity — verify-spec for two pages composes
    // different bodies. Stable stringify keeps the key deterministic.
    queryKey: ["composed-prompt", promptName, JSON.stringify(args)],
    queryFn: () => mcpClient.getComposedPrompt(promptName, args),
    enabled: open && !!promptName,
    staleTime: 10_000,
  });

  if (!open) return null;

  const prompt = promptQuery.data ?? "";

  async function copy(guidance?: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      showToast(guidance ?? "Prompt copied to clipboard");
    } catch {
      showToast("Couldn't copy to clipboard", "error", true);
    }
  }

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...modalBox, width: 640 }} role="dialog" aria-label={title}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={iconBox}>
            <i className={`ti ti-${icon}`} aria-hidden="true" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
              {title}
            </div>
            {subtitle ? (
              <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{subtitle}</div>
            ) : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={closeBtn}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>

        {promptQuery.isLoading ? (
          <div style={{ ...codeBlock, color: "var(--color-text-tertiary)" }}>Composing prompt…</div>
        ) : promptQuery.error ? (
          <div style={{ ...codeBlock, color: "var(--c-red-600)" }}>
            Couldn't compose the prompt:{" "}
            {promptQuery.error instanceof Error ? promptQuery.error.message : "error"}
          </div>
        ) : (
          <pre style={codeBlock}>{prompt}</pre>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => copy("Copied — paste into Claude Desktop or claude.ai")}
            disabled={!prompt}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            <i className="ti ti-sparkles" style={{ fontSize: 14, marginRight: 4 }} aria-hidden="true" /> Open in Claude
          </button>
          <button
            type="button"
            onClick={() => copy("Copied — paste into Cursor")}
            disabled={!prompt}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            <i className="ti ti-terminal-2" style={{ fontSize: 14, marginRight: 4 }} aria-hidden="true" /> Open in Cursor
          </button>
          <button
            type="button"
            data-variant="primary"
            onClick={() => copy()}
            disabled={!prompt}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            <i className="ti ti-copy" style={{ fontSize: 14, marginRight: 4 }} aria-hidden="true" /> Copy prompt
          </button>
        </div>
      </div>
    </div>
  );
}

const iconBox: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  fontSize: 20,
  flexShrink: 0,
};

const closeBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  padding: 4,
  display: "inline-flex",
  flexShrink: 0,
};

const codeBlock: CSSProperties = {
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  padding: 14,
  maxHeight: "50vh",
  overflowY: "auto",
  margin: 0,
  color: "var(--color-text-primary)",
};
